import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

const FIRST_PLACEMENT_SETTLE_MS = 50;

type Placement = { left: number; width: number };

/**
 * Drives the shared highlight behind a row of tabs, placed on the selected tab from its `offsetLeft` and
 * `offsetWidth` and re-measured whenever the list or a tab changes size. The labels are never moved.
 *
 * The highlight is `instant` for its first placement after the tab list mounts, so it does not slide in
 * from the left. `instant` drops after a short settle time (a few frames, so the browser has styled the
 * first placement before transitions switch on).
 */
export function useTabHighlights<TabId extends string>(tabIds: readonly TabId[], selected: TabId) {
  const tabElements = useRef(new Map<TabId, HTMLElement>());
  const listElement = useRef<HTMLElement | null>(null);
  const [placements, setPlacements] = useState<Partial<Record<TabId, Placement>>>({});
  const [instant, setInstant] = useState(true);
  const [selection, setSelection] = useState({ tab: selected, steps: 1 });

  if (selection.tab !== selected) {
    setSelection({ tab: selected, steps: tabsBetween(tabIds, selection.tab, selected) });
  }

  const measure = useCallback(() => {
    const next: Partial<Record<TabId, Placement>> = {};
    for (const [id, element] of tabElements.current) {
      next[id] = { left: element.offsetLeft, width: element.offsetWidth };
    }
    setPlacements(previous => (samePlacements(previous, next) ? previous : next));
  }, []);

  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    if (listElement.current) observer.observe(listElement.current);
    for (const element of tabElements.current.values()) observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  useEffect(() => {
    if (!instant) return;
    const timer = window.setTimeout(() => setInstant(false), FIRST_PLACEMENT_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [instant]);

  function registerList(element: HTMLElement | null) {
    listElement.current = element;
  }

  function registerTab(id: TabId, element: HTMLElement | null) {
    if (element) tabElements.current.set(id, element);
    else tabElements.current.delete(id);
  }

  function highlightStyle(id: TabId): CSSProperties {
    const placement = placements[id];
    if (!placement) return {};
    return { width: placement.width, transform: `translateX(${placement.left}px)` };
  }

  function selectedHighlightStyle(): CSSProperties {
    return { ...highlightStyle(selected), "--highlight-steps": selection.steps } as CSSProperties;
  }

  return {
    registerList,
    registerTab,
    selectedHighlight: { tab: selected, style: selectedHighlightStyle(), instant },
  };
}

function tabsBetween<TabId extends string>(
  tabIds: readonly TabId[],
  from: TabId,
  to: TabId,
): number {
  return Math.max(1, Math.abs(tabIds.indexOf(to) - tabIds.indexOf(from)));
}

function samePlacements<TabId extends string>(
  a: Partial<Record<TabId, Placement>>,
  b: Partial<Record<TabId, Placement>>,
): boolean {
  const keys = Object.keys(b) as TabId[];
  if (Object.keys(a).length !== keys.length) return false;
  return keys.every(key => a[key]?.left === b[key]?.left && a[key]?.width === b[key]?.width);
}
